/**
 * Facebook SDK Plugin for Nuxt.js
 * ปลั๊กอินสำหรับโหลด Facebook SDK และจัดการการตั้งค่าแบบ traditional script loading
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Only run on client side
  if (process.server) return

  console.log('🔵 Facebook SDK Plugin: Initializing...')

  // Get Facebook configuration - ใช้ fallback methods หลายแบบ
  let FACEBOOK_CONFIG = {
    appId: '',
    version: 'v18.0',
    cookie: true,
    xfbml: true,
    locale: 'en_US'
  }

  // Method 1: Try to get from injected configs
  const getConfigFromInjected = () => {
    try {
      const configs = nuxtApp.$config || {}
      if (configs.loginConfig?.facebookConfigs?.length > 0) {
        const selectedIndex = configs.loginConfig.selectedFacebookConfigIndex || 0
        return configs.loginConfig.facebookConfigs[selectedIndex]
      }
    } catch (err) {
      console.log('⚠️ Cannot get config from injected data:', err.message)
    }
    return null
  }

  // Method 2: Try to get from window.configs (global)
  const getConfigFromWindow = () => {
    try {
      if (typeof window !== 'undefined' && window.configs?.loginConfig?.facebookConfigs?.length > 0) {
        const selectedIndex = window.configs.loginConfig.selectedFacebookConfigIndex || 0
        return window.configs.loginConfig.facebookConfigs[selectedIndex]
      }
    } catch (err) {
      console.log('⚠️ Cannot get config from window:', err.message)
    }
    return null
  }

  // Method 3: Hardcode fallback for testing
  const getFallbackConfig = () => {
    return {
      name: 'Dev',
      appId: '765969019749786',
      appSecret: 'd31cb38c998497563d45297aef6839ce',
      redirectUri: 'https://frontend.dev.test:3000/user/authen/facebook/callback'
    }
  }

  // Try different methods to get config
  let selectedConfig = getConfigFromInjected() || getConfigFromWindow() || getFallbackConfig()

  if (selectedConfig && selectedConfig.appId) {
    FACEBOOK_CONFIG.appId = selectedConfig.appId
    console.log('🟢 Facebook Config loaded:', {
      source: selectedConfig === getFallbackConfig() ? 'fallback' : 'config',
      name: selectedConfig.name,
      appId: selectedConfig.appId.substring(0, 8) + '...',
      redirectUri: selectedConfig.redirectUri
    })
  } else {
    console.log('⚠️ No valid Facebook config found, using fallback')
    selectedConfig = getFallbackConfig()
    FACEBOOK_CONFIG.appId = selectedConfig.appId
  }

  /**
   * Load Facebook SDK using traditional method
   */
  const loadFacebookSDK = () => {
    return new Promise((resolve, reject) => {
      try {
        // Check if SDK already loaded
        if (window.FB) {
          console.log('🟡 Facebook SDK already exists')
          resolve(window.FB)
          return
        }

        console.log('🔵 Loading Facebook SDK using traditional method...')

        // Traditional Facebook SDK initialization
        window.fbAsyncInit = function() {
          console.log('🟢 Facebook SDK loaded, initializing with fbAsyncInit...')
          
          FB.init({
            appId: FACEBOOK_CONFIG.appId,
            cookie: FACEBOOK_CONFIG.cookie,
            xfbml: FACEBOOK_CONFIG.xfbml,
            version: FACEBOOK_CONFIG.version
          })

          // Log page view
          FB.AppEvents.logPageView()

          console.log('🟢 Facebook SDK initialized successfully:', {
            appId: FACEBOOK_CONFIG.appId ? FACEBOOK_CONFIG.appId.substring(0, 8) + '...' : 'Not set',
            version: FACEBOOK_CONFIG.version,
            cookie: FACEBOOK_CONFIG.cookie,
            xfbml: FACEBOOK_CONFIG.xfbml
          })

          resolve(window.FB)
        }

        // Traditional Facebook SDK loading script
        ;(function(d, s, id) {
          var js, fjs = d.getElementsByTagName(s)[0]
          if (d.getElementById(id)) {
            console.log('🟡 Facebook SDK script already exists')
            return
          }
          js = d.createElement(s)
          js.id = id
          js.src = `https://connect.facebook.net/${FACEBOOK_CONFIG.locale}/sdk.js`
          js.async = true
          js.defer = true
          
          // Handle script load error
          js.onerror = () => {
            console.error('🔴 Failed to load Facebook SDK script')
            reject(new Error('Failed to load Facebook SDK'))
          }
          
          fjs.parentNode.insertBefore(js, fjs)
          console.log('🔵 Facebook SDK script inserted into DOM')
        })(document, 'script', 'facebook-jssdk')

        // Backup timeout in case fbAsyncInit doesn't fire
        setTimeout(() => {
          if (!window.FB) {
            console.error('🔴 Facebook SDK timeout - fbAsyncInit did not fire')
            reject(new Error('Facebook SDK initialization timeout'))
          }
        }, 15000) // 15 second timeout

      } catch (error) {
        console.error('🔴 Error loading Facebook SDK:', error)
        reject(error)
      }
    })
  }

  /**
   * Check Facebook SDK status
   */
  const checkFacebookStatus = () => {
    if (!window.FB) {
      console.warn('⚠️ Facebook SDK not loaded')
      return false
    }

    // Check login status
    FB.getLoginStatus((response) => {
      console.log('🔵 Facebook login status:', response.status)
      
      switch (response.status) {
        case 'connected':
          console.log('🟢 User is logged in to Facebook and authorized app')
          break
        case 'not_authorized':
          console.log('🟡 User is logged in to Facebook but not authorized app')
          break
        default:
          console.log('🔴 User is not logged in to Facebook')
          break
      }
    })

    return true
  }

  /**
   * Get Facebook debug info
   */
  const getFacebookDebugInfo = () => {
    return {
      isLoaded: !!window.FB,
      config: FACEBOOK_CONFIG,
      appId: FACEBOOK_CONFIG.appId,
      version: FACEBOOK_CONFIG.version,
      hasAppId: !!FACEBOOK_CONFIG.appId,
      url: window.location.href
    }
  }

  // Provide Facebook utilities to the app
  nuxtApp.provide('facebook', {
    loadSDK: loadFacebookSDK,
    checkStatus: checkFacebookStatus,
    getDebugInfo: getFacebookDebugInfo,
    config: FACEBOOK_CONFIG
  })

  // Auto-load SDK if app ID is configured
  if (FACEBOOK_CONFIG.appId) {
    // Load SDK when app is mounted
    nuxtApp.hook('app:mounted', async () => {
      try {
        console.log('🔵 Facebook SDK Plugin: Auto-loading SDK...')
        await loadFacebookSDK()
        console.log('🟢 Facebook SDK Plugin: SDK loaded successfully')
        
        // Check initial status
        setTimeout(() => {
          checkFacebookStatus()
        }, 1000)
        
      } catch (error) {
        console.error('🔴 Facebook SDK Plugin: Failed to auto-load SDK:', error)
      }
    })
  } else {
    console.warn('⚠️ Facebook SDK Plugin: No App ID configured, skipping auto-load')
  }

  console.log('🟢 Facebook SDK Plugin: Plugin registered successfully')
})